import { TripsApi } from '@/shared/api'
import {
  CreateTripDto,
  CreateQuickTripDto,
  UpdateTripDto,
  CreateWaypointDto,
  UpdateWaypointDto,
  AddMemberDto,
  AddTagAlongDto,
  UpdateMemberRoleDto,
  TripQueryDto,
  TripResponse,
  TripWithDetailsResponse,
  WaypointResponse,
  MemberResponse,
  PaginatedResponse,
  TripWeatherLocation,
  CampspotterAssessment
} from '@/shared/api'

// Trip service class
export class TripService {
  private tripsApi: TripsApi

  constructor(getAuthToken: () => Promise<string | null>) {
    this.tripsApi = new TripsApi(getAuthToken)
  }

  // Trip CRUD operations
  async createTrip(tripData: CreateTripDto): Promise<TripResponse> {
    return this.tripsApi.createTrip(tripData)
  }

  async createQuickSpontaneousTrip(quickTripData: CreateQuickTripDto): Promise<TripResponse> {
    return this.tripsApi.createQuickSpontaneousTrip(quickTripData)
  }

  async getTrips(
    query?: TripQueryDto & {
      includeArchived?: boolean
      archivedOnly?: boolean
      sort_by?: 'start_date' | 'end_date' | 'updated_at' | 'created_at' | 'name'
      sort_order?: 'asc' | 'desc'
    }
  ): Promise<PaginatedResponse<TripResponse>> {
    return this.tripsApi.getTrips(query)
  }

  async getTrip(tripId: string): Promise<TripWithDetailsResponse> {
    return this.tripsApi.getTrip(tripId)
  }

  async getTripAsGuest(tripId: string, inviteCode: string, guestName: string, accessToken?: string): Promise<TripWithDetailsResponse> {
    return this.tripsApi.getTripAsGuest(tripId, inviteCode, guestName, accessToken)
  }

  async updateTrip(tripId: string, tripData: UpdateTripDto): Promise<TripResponse> {
    return this.tripsApi.updateTrip(tripId, tripData)
  }

  async deleteTrip(tripId: string): Promise<void> {
    return this.tripsApi.deleteTrip(tripId)
  }

  // Waypoint operations
  async addWaypoint(tripId: string, waypointData: CreateWaypointDto): Promise<WaypointResponse> {
    return this.tripsApi.addWaypoint(tripId, waypointData)
  }

  async updateWaypoint(
    tripId: string,
    waypointId: string,
    waypointData: UpdateWaypointDto
  ): Promise<WaypointResponse> {
    return this.tripsApi.updateWaypoint(tripId, waypointId, waypointData)
  }

  async deleteWaypoint(tripId: string, waypointId: string): Promise<void> {
    return this.tripsApi.deleteWaypoint(tripId, waypointId)
  }

  async reorderWaypoints(tripId: string, waypointIds: string[]): Promise<void> {
    return this.tripsApi.reorderWaypoints(tripId, waypointIds)
  }

  // Member operations
  async addMember(tripId: string, memberData: AddMemberDto): Promise<MemberResponse> {
    return this.tripsApi.addMember(tripId, memberData)
  }

  async addTagAlong(tripId: string, parentMemberId: string, data: AddTagAlongDto): Promise<MemberResponse> {
    return this.tripsApi.addTagAlong(tripId, parentMemberId, data)
  }

  async updateMemberRole(
    tripId: string,
    memberId: string,
    roleData: UpdateMemberRoleDto
  ): Promise<MemberResponse> {
    return this.tripsApi.updateMemberRole(tripId, memberId, roleData)
  }

  async removeMember(tripId: string, memberId: string): Promise<void> {
    return this.tripsApi.removeMember(tripId, memberId)
  }

  async leaveTrip(tripId: string): Promise<void> {
    return this.tripsApi.leaveTrip(tripId)
  }

  // Public trips
  async getPublicTrips(query?: Omit<TripQueryDto, 'is_public'>): Promise<PaginatedResponse<TripResponse>> {
    return this.tripsApi.getPublicTrips(query)
  }

  // Trip search
  async searchTrips(query: string, filters?: Omit<TripQueryDto, 'search'>): Promise<PaginatedResponse<TripResponse>> {
    return this.tripsApi.searchTrips(query, filters)
  }

  // Utility methods for data transformation
  formatTripDates(trip: TripResponse): TripResponse {
    return {
      ...trip,
      start_date: trip.start_date ? new Date(trip.start_date).toISOString().split('T')[0] : null,
      end_date: trip.end_date ? new Date(trip.end_date).toISOString().split('T')[0] : null
    }
  }

  sortWaypointsByOrder(waypoints: WaypointResponse[]): WaypointResponse[] {
    return [...waypoints].sort((a, b) => {
      const orderA = a.order_index ?? 0
      const orderB = b.order_index ?? 0
      return orderA - orderB
    })
  }

  getTripDuration(trip: TripResponse): number | null {
    if (!trip.start_date || !trip.end_date) return null
    
    const start = new Date(trip.start_date)
    const end = new Date(trip.end_date)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return diffDays
  }

  isTripActive(trip: TripResponse): boolean {
    if (!trip.start_date || !trip.end_date) return false
    
    const now = new Date()
    const start = new Date(trip.start_date)
    const end = new Date(trip.end_date)
    
    return now >= start && now <= end
  }

  isTripUpcoming(trip: TripResponse): boolean {
    if (!trip.start_date) return false
    
    const now = new Date()
    const start = new Date(trip.start_date)
    
    return now < start
  }

  isTripPast(trip: TripResponse): boolean {
    if (!trip.end_date) return false
    
    const now = new Date()
    const end = new Date(trip.end_date)
    
    return now > end
  }

  // Archive operations
  async archiveTrip(tripId: string): Promise<void> {
    return this.tripsApi.archiveTrip(tripId)
  }

  async unarchiveTrip(tripId: string): Promise<void> {
    return this.tripsApi.unarchiveTrip(tripId)
  }

  async restoreTripFromRetention(tripId: string): Promise<TripResponse> {
    return this.tripsApi.restoreTripFromRetention(tripId)
  }

  // Weather operations
  async getTripWeather(tripId: string): Promise<TripWeatherLocation[]> {
    return this.tripsApi.getTripWeather(tripId)
  }

  /**
   * Manual fetch: call Campspotter for all weather waypoints, return normalized assessments.
   * Pro/Team only.
   */
  async fetchTripWeather(tripId: string): Promise<CampspotterAssessment[]> {
    return this.tripsApi.fetchTripWeather(tripId)
  }

  /**
   * Test endpoint: call Campspotter with this trip's first waypoint and dates, return raw response.
   */
  async getCampspotterTest(tripId: string): Promise<{
    success: boolean
    ok?: boolean
    data?: unknown
    request?: { lat: number; lon: number; startDate: string; endDate: string; spotName?: string }
    error?: string
    details?: { status?: number; statusText?: string; errorBody?: string }
  }> {
    return this.tripsApi.getCampspotterTest(tripId)
  }
}
